import { Pattern } from "../../pattern/pattern";
import { isPatternParsingError, patternAsString, PatternParsingError, patternParsingErrorToString, stringAsPattern } from "../../pattern/serializePattern";
import { IpaType } from "../../phonemes/ipa";
import { SyllablePhonotactics } from "../../phonotactics/phonotactics";
import { SegmentStrategy } from "../../rules/intoSegments";
import { LengthRules } from "../../rules/lengthRules";
import { Rule } from "../../rules/rule";
import { PreprocessOptions, RuleComment, Rules2Type, RuleSet, RuleType } from "../../rules/ruleSet";
import { isRuleParsingError, RuleParsingError, ruleParsingErrorToString, ruleToString, stringToRule } from "../../rules/serializeRule";
import { StressMatch, StressRule } from "../../stress/stressRule";

/** serialize the subset of RuleSet used for ortho rules to a simple format */
export function orthoRulesToString(ruleSet: RuleSet): string {
  const preprocess = ruleSet.preprocess === undefined ? ''
    : `preprocess: ${ruleSet.preprocess.join(', ')}\n`;
  const inputSegments = ruleSet.inputSegments === undefined ? ''
    : `inputSegments: ${ruleSet.inputSegments}\n`;
  const outputSegments = ruleSet.outputSegments === undefined ? ''
    : `outputSegments: ${ruleSet.outputSegments}\n`;
  const inputSegments2 = ruleSet.inputSegments2 === undefined ? ''
    : `inputSegments2: ${ruleSet.inputSegments2}\n`;
  const outputSegments2 = ruleSet.outputSegments2 === undefined ? ''
    : `outputSegments2: ${ruleSet.outputSegments2}\n`;

  const rules = ruleSet.rules === undefined ? ''
    : rulesToString(ruleSet.rules);
  const rules2 = ruleSet.rules2 === undefined ? ''
    : multiRulesToString(ruleSet.rules2);
  const phonemeLengths = ruleSet.phonemeLengths === undefined ? ''
    : lengthRulesToString(ruleSet.phonemeLengths);
  const stressRule = ruleSet.stressRule === undefined ? ''
    : stressRuleToString(ruleSet.stressRule);
  const phonotactics = ruleSet.phonotactics === undefined ? ''
    : phonotacticsToString(ruleSet.phonotactics);

  return `${preprocess}${inputSegments}${outputSegments}${inputSegments2}${outputSegments2}${rules}${rules2}${phonemeLengths}${stressRule}${phonotactics}`;
}

function rulesToString(rules: RuleType[], label?: string): string {
  const all = rules.map(r => ruleToString(r));
  if (all.some(s => s === undefined)) {
    console.log('unsupported rule');
    return '';
  } else if (all.length === 0) {
    return '';
  }

  label = label ?? 'rules:'
  return `${label} [\n ${all.join('\n ')}\n]\n`;
}

function multiRulesToString(ruleSets: Rules2Type): string {
  if (ruleSets === undefined || ruleSets.length === 0) {
    return '';
  }

  // single set of rules
  if (!Array.isArray(ruleSets[0])) {
    return rulesToString(ruleSets as RuleType[], 'rules2:');
  }

  // multiple sets of rules
  ruleSets = ruleSets as RuleType[][];
  const sets: string[] = [];
  for (const rs of ruleSets) {
    if (rs && rs.length > 0) {
      const list = rs.map(r => ` ${ruleToString(r)}`);
      if (list.every(s => s !== undefined)) {
        sets.push(list.join('\n'));
      }
    }
  }
  // each set of rules is separated by "reset"
  const all = sets.join('\n reset\n');
  return `rules2: [\n${all}\n]\n`;
}

function lengthRulesToString(lengthRules: LengthRules): string {
  if (lengthRules.defaultDiphthongMS !== undefined) {
    console.log('ruleSet.phonemeLengths.defaultDiphthongMS unsupported');
  }
  if (lengthRules.adds !== undefined) {
    console.log('ruleSet.phonemeLengths.adds unsupported');
  }

  const map = lengthRules.map;
  const phonemes = Object.getOwnPropertyNames(map);
  if (phonemes.length === 0) {
    return '';
  }

  const lengths = phonemes.map(p => `${p}: ${map[p]}`).join(', ');
  return `lengths: {\n map: { ${lengths} }\n}\n`;
}

function stressRuleToString(stressRule: StressRule<string>): string {
  if (stressRule.move !== undefined) {
    console.log('ruleSet.stressRule.move unsupported');
  }

  const toCheck = stressCheckToString(stressRule.toCheck);
  const s = `stressRule: {\n default: ${stressRule.default}\n${toCheck}}\n`;
  return s;
}
function stressCheckToString(toCheck?: StressMatch<string>[]): string {
  if (toCheck === undefined || toCheck.length === 0) {
    return '';
  }
  const lines = toCheck.map(tc => `  ${tc.which} ${patternAsString(tc.pattern)}`).join('\n');
  return ` toCheck: {\n${lines}\n }\n`;
}

function phonotacticsToString(phonotactics: SyllablePhonotactics<IpaType>): string {
  const affricates = phonotactics.affricates !== undefined ? ` affricates: ${phonotactics.affricates}\n` : '';
  const onset = patternAsString(phonotactics.onset);
  const nucleus = patternAsString(phonotactics.nucleus);
  const coda = patternAsString(phonotactics.coda);
  const onsetStr = onset === undefined || onset.length === 0 ? ''
    : ` onset: ${onset}\n`;
  const nucleusStr = nucleus === undefined || nucleus.length === 0 ? ''
    : ` nucleus: ${nucleus}\n`;
  const codaStr = coda === undefined || coda.length === 0 ? ''
    : ` coda: ${coda}\n`;
  return `phonotactics: {\n${affricates}${onsetStr}${nucleusStr}${codaStr}}\n`;
}


/** parse the subset of RuleSet used for ortho rules to a simple format */
export function stringToOrthoRules(s: string): RuleSet | OrthoRulesError {
  const lines = s.split('\n');

  const preprocess = parsePreprocess(lines);
  const inputSegments = getLabeledText(lines, 'inputSegments:') as SegmentStrategy;
  const outputSegments = getLabeledText(lines, 'outputSegments:') as SegmentStrategy;
  const inputSegments2 = getLabeledText(lines, 'inputSegments2:') as SegmentStrategy;
  const outputSegments2 = getLabeledText(lines, 'outputSegments2:') as SegmentStrategy;

  const rules = parseSingleRules(lines);
  const rules2 = parseMultiRules(lines);
  const phonemeLengths = parsePhonemeLengths(lines);
  const stressRule = parseStressRule(lines);
  const phonotactics = parsePhonotactics(lines);

  if (isOrthoRulesError(rules)) { return rules; }
  if (isOrthoRulesError(rules2)) { return rules2; }
  if (isOrthoRulesError(phonemeLengths)) { return phonemeLengths; }
  if (isOrthoRulesError(stressRule)) { return stressRule; }
  if (isOrthoRulesError(phonotactics)) { return phonotactics; }
  const ruleSet: RuleSet = { preprocess, inputSegments, outputSegments, inputSegments2, outputSegments2, rules, rules2, phonemeLengths, stressRule, phonotactics };
  return removeUndefined(ruleSet);
}

export interface OrthoRulesError {
  readonly line: number;
  readonly orthoError:
  | 'expected map{}'
  | 'expected vowels:length'
  | 'invalid length'
  | 'missing }'
  | 'error parsing rule'
  | 'error parsing pattern'
  ;
  readonly context?: string;
  readonly ruleParsingError?: RuleParsingError;
  readonly patternParsingError?: PatternParsingError;
}
export function isOrthoRulesError(check: unknown): check is OrthoRulesError {
  const test = check as OrthoRulesError;
  return test && (test.line !== undefined || test.orthoError !== undefined);
}
export function orthoRulesErrorToString(error: OrthoRulesError): string {
  let str = `error line ${error.line} - ${error.orthoError}`;
  if (error.context) {
    str += `, context: "${error.context}"`;
  }
  if (error.ruleParsingError) {
    str += `, ${ruleParsingErrorToString(error.ruleParsingError)}`;
  }
  if (error.patternParsingError) {
    str += `, ${patternParsingErrorToString(error.patternParsingError)}`;
  }
  return str;
}

function parsePreprocess(lines: string[]): PreprocessOptions[] | undefined {
  const value = getLabeledText(lines, 'preprocess:');
  if (value === undefined) {
    return undefined;
  }
  const options = value.split(',').map(s => s.trim());
  return options as PreprocessOptions[];
}

function parseSingleRules(lines: string[]): RuleType[] | OrthoRulesError | undefined {
  const rules = parseRules(lines);
  if (rules === undefined || isOrthoRulesError(rules)) {
    return rules;
  }
  return rules.filter(r => r !== 'reset') as RuleType[];
}

function parseMultiRules(lines: string[]): Rules2Type | OrthoRulesError | undefined {
  const rules = parseRules(lines, 'rules2:');
  if (rules === undefined || isOrthoRulesError(rules)) {
    return rules;
  }
  if (rules.every(r => r !== 'reset')) {
    return rules as RuleType[]; // single list
  }
  // multiple lists
  const lists: RuleType[][] = [];
  let list: RuleType[] = [];
  for (const rule of rules) {
    if (rule === 'reset') {
      if (list.length > 0) {
        lists.push(list);
        list = [];
      }
    } else {
      list.push(rule);
    }
  }
  if (list.length > 0) {
    lists.push(list);
  }
  return lists;
}

function parseRules(lines: string[], label?: string): (Rule | RuleComment | 'reset')[] | OrthoRulesError | undefined {
  const start = lines.findIndex(line => line.startsWith(label ?? 'rules:'));
  if (start === -1) {
    return undefined;
  }

  const rules: (Rule | RuleComment | 'reset')[] = [];
  for (let i = start + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line === ']' || line === '}') { // should be ] but } is acceptable
      return rules;
    }
    const rule = line === 'reset' ? 'reset' : stringToRule(line);
    if (isRuleParsingError(rule)) {
      return { line: i + 1, orthoError: 'error parsing rule', ruleParsingError: rule };
    }
    rules.push(rule);
  }
  return rules.length === 0 ? undefined : rules;
}

function parsePhonemeLengths(lines: string[]): LengthRules | OrthoRulesError | undefined {
  const start = lines.findIndex(line => line.startsWith('lengths:'));
  if (start === -1 || start === lines.length - 1) {
    return undefined;
  }

  const line = lines[start + 1].trim();
  const brace = line.indexOf('{');
  if (brace === -1 || line[line.length - 1] !== '}') {
    return { line: start + 1, orthoError: 'expected map{}' };
  }

  const content = line.substring(brace + 1, line.length - 1);
  const pieces = content.split(',');
  const map: Record<IpaType, number> = {};
  for (const piece of pieces) {
    const pair = piece.split(':');
    if (pair.length !== 2) {
      return { line: start + 2, orthoError: 'expected vowels:length', context: piece.trim() };
    }
    const vowel = pair[0].trim();
    if (vowel.length === 0 || pair[1].length === 0) {
      return { line: start + 2, orthoError: 'expected vowels:length', context: piece.trim() };
    }
    const length = Number.parseInt(pair[1]);
    if (isNaN(length) || length <= 0) {
      return { line: start + 2, orthoError: 'invalid length', context: piece.trim() };
    }
    map[vowel] = length;
  }
  return { map };
}

function parseStressRule(lines: string[]): StressRule<IpaType> | OrthoRulesError | undefined {
  const start = lines.findIndex(line => line.startsWith('stressRule:'));
  if (start === -1 || start === lines.length - 1) {
    return undefined;
  }
  const end = findStressEnd(lines, start);
  if (isOrthoRulesError(end)) {
    return end;
  }
  const subLines = lines.slice(start + 1, end);

  // default
  let defaultValue = 1;
  const iDefault = subLines.findIndex(line => line.trim().startsWith('default:'));
  if (iDefault !== -1) {
    const rest = subLines[iDefault].substring('default:'.length + 1);
    const n = Number.parseInt(rest);
    if (!isNaN(n)) {
      defaultValue = n;
    }
  }

  // toCheck patterns
  let toCheck: StressMatch<IpaType>[] = [];
  const iToCheck = subLines.findIndex(line => line.trim().startsWith('toCheck:'));
  if (iToCheck !== -1) {
    for (let i = iToCheck + 1; i < subLines.length; i++) {
      const line = subLines[i].trim();
      if (line === '}') {
        break;
      }
      const pairs = line.split(' ', 2);
      const which = Number.parseInt(pairs[0]);
      const pattern = stringAsPattern(pairs[1]);
      if (isPatternParsingError(pattern)) {
        return { line: start + i + 2, orthoError: 'error parsing pattern', patternParsingError: pattern };
      } else if (pattern === null) {
        return { line: start + i + 2, orthoError: 'error parsing pattern', context: line };
      }
      toCheck.push({ which, pattern });
    }
  }

  return toCheck.length === 0 ? { default: defaultValue } : { default: defaultValue, toCheck };
}
function findStressEnd(lines: string[], start: number): number | OrthoRulesError {
  let count = 0;
  for (let i = start; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.endsWith('{')) {
      count++;
    } else if (line === '}') {
      count--;
    }
    if (count <= 0) {
      return i; // found the end
    }
  }
  return { line: start + 1, orthoError: 'missing }' };
}

function parsePhonotactics(lines: string[]): SyllablePhonotactics<IpaType> | OrthoRulesError | undefined {
  const start = lines.findIndex(line => line.startsWith('phonotactics:'));
  if (start === -1 || start === lines.length - 1) {
    return undefined;
  }

  let affricates: 'all-aff' | 'en-aff' | undefined;
  let onset: Pattern<string> | PatternParsingError | null = null;
  let nucleus: Pattern<string> | PatternParsingError | null = null;
  let coda: Pattern<string> | PatternParsingError | null = null;
  for (let i = start + 1; i < start + 5; i++) {
    const line = lines[i].trim();
    const pair = line.split(':', 2).map(p => p.trim());
    if (pair[0] === 'affricates') {
      if (pair[1] === 'all-aff' || pair[1] === 'en-aff') {
        affricates = pair[1];
      }
    } else {
      const pattern = stringAsPattern(pair[1]);
      if (isPatternParsingError(pattern)) {
        return { line: start + i + 1, orthoError: 'error parsing pattern', patternParsingError: pattern };
      }
      if (pair[0] === 'onset') {
        onset = pattern;
      } else if (pair[0] === 'nucleus') {
        nucleus = pattern;
      } else if (pair[0] === 'coda') {
        coda = pattern;
      }
    }
  }

  return {
    affricates,
    onset: onset === null ? '' : onset,
    nucleus: nucleus === null ? '' : nucleus,
    coda: coda === null ? '' : coda,
  };
}

/** get the text attached to a label on a single line */
function getLabeledText(lines: string[], label: string): string | undefined {
  for (const line of lines) {
    if (line.startsWith(label)) {
      const value = line.substring(label.length).trim();
      return value === '' ? undefined : value;
    }
  }
  return undefined;
}

/** remove all fields with a value of 'undefined' */
function removeUndefined(rules: RuleSet): RuleSet {
  const oldObject = rules as Record<string, object>;
  const newObject: Record<string, object> = {};
  const keys = Object.getOwnPropertyNames(rules);
  for (const key of keys) {
    if (oldObject[key] !== undefined) {
      newObject[key] = oldObject[key];
    }
  }
  return newObject;
}
